import { observer } from 'mobx-react-lite';
import './questCard.styl';
import { IRewardState, ITaskConfig, QuestRewardTypes, QuestTypes } from 'src/types';
import { Button } from 'components/core';
import { useTranslationsContext } from 'store/translations';
import Ticket from 'assets/img/ticket.png';
import { useStoreContext } from 'store/app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import OKSDK from '@odnoklassniki/ok-apps-sdk';
import { QuestInfoModal } from 'components/modal/questInfoModal';
import { StorageKeys } from 'utils/storageKeys';
import { useNavigate } from 'react-router';
import { routes } from 'utils/router';
import { RewardDetailsModal } from 'components/modal/rewardsModal/rewardDetailsModal';
import { get14DaysModalData } from 'utils/get14DaysModalData';
import classNames from 'classnames';

interface Props {
    mission: ITaskConfig;
}

export const QuestCard = observer(({ mission }: Props) => {
    const { translate } = useTranslationsContext();
    const {
        userState,
        completeQuest,
        setUserVariable,
        userVariables,
        showToast,
        getVisitedDaysAmount,
        logOperation,
        hasInvitableFirends,
        setCheckOverlays
    } = useStoreContext();
    const [openQuestInfoModal, setOpenQuestInfoModal] = useState(false);
    const [loginsModalReward, setLoginsModalReward] = useState<IRewardState>();
    const navigate = useNavigate();

    const visitedDays = useMemo(() => {
        return new Set([...(userState?.visited_days || []), userState?.day]).size;
    }, [userState]);

    const {
        background,
        title,
        buttonText,
        rewardType,
        newYearType,
        goal,
        children,
        img,
        questCompletePrompt,
        key,
        logRender
    } = mission;

    const isDisabledInvitesTask = useMemo(
        () => newYearType === QuestTypes.INVITE && !hasInvitableFirends,
        [hasInvitableFirends, mission]
    );

    useEffect(() => {
        logRender && logOperation('tasks', 'render_task', key);
    }, []);

    const getRewardBubble = () => {
        if (rewardType === QuestRewardTypes.SNAKE_2025_TICKET) {
            return (
                <span className="quest-card-reward-bubble">
                    <img className="quest-card-reward-ticket" src={Ticket}></img>
                    {translate('plus.ticket')}
                </span>
            );
        }

        if (rewardType === QuestRewardTypes.SNAKE_2025_GIFT) {
            return <span className="quest-card-reward-bubble">{translate('plus.gift')}</span>;
        }

        if (rewardType === QuestRewardTypes.SNAKE_2025_LOOTBOX || rewardType === QuestRewardTypes.SNAKE_2025) {
            return <span className="quest-card-reward-bubble">{translate('plus.toy')}</span>;
        }

        if (rewardType === QuestRewardTypes.SNAKE_2025_PROMO) {
            return <span className="quest-card-reward-bubble">{translate('plus.promo')}</span>;
        }

        return null;
    };

    const getCounter = () => {
        if (newYearType === QuestTypes.LOGINS) {
            return (
                <div className="quest-card-counter">
                    {translate('counter', { count: visitedDays, total: goal || 14 })}
                </div>
            );
        }

        if (newYearType === QuestTypes.INVITE) {
            const capacityInvites = children![0].capacity;
            const doneInvites = children![0].done;
            return (
                <div className="quest-card-counter">
                    {isDisabledInvitesTask
                        ? translate('no.friends.desc')
                        : translate('counter', { count: doneInvites, total: capacityInvites || 5 })}
                </div>
            );
        }

        return null;
    };

    const onButtonClick = useCallback(() => {
        if (newYearType === QuestTypes.LOGINS) {
            const daysLeft = mission.goal! - getVisitedDaysAmount();
            setLoginsModalReward(get14DaysModalData(translate, mission, daysLeft, mission.goal || 14));
            return;
        }

        if (newYearType === QuestTypes.INTERNAL || newYearType === QuestTypes.LINK) {
            setOpenQuestInfoModal(true);
        }

        if (newYearType === QuestTypes.INVITE) {
            logOperation('tasks', 'task_open', mission.key);
            const alreadyInvitedUsers = userVariables?.invited_users?.split(',') ?? [];
            OKSDK.Methods.Layers.showInvite(translate('invite.text'), '', '').then(({ status, data }) => {
                const invites = (data || '')
                    .toString()
                    .split(',')
                    .filter((id) => id && !alreadyInvitedUsers.includes(id));
                if (status === 'ok' && invites.length) {
                    completeQuest(children![0].key, invites.length);
                    setUserVariable(StorageKeys.INVITED_USERS, [...alreadyInvitedUsers, ...invites].join(','));
                    if (children![0].goal || 5 <= children![0].done + invites.length) {
                        showToast(questCompletePrompt || translate('quest.complete'));
                        if (rewardType === QuestRewardTypes.SNAKE_2025_LOOTBOX) {
                            setCheckOverlays(true);
                        } else {
                            navigate(routes.wheel, { state: { missionId: key } });
                        }
                    }
                }
            });
        }
    }, [userVariables, mission]);

    return (
        <>
            <div
                onClick={buttonText ? undefined : onButtonClick}
                className={classNames('quest-card', { 'with-button': !!buttonText })}
                style={{ background: background }}
            >
                <div className="quest-card-content">
                    <div className="quest-card-content-left">
                        <div className="quest-card-title">
                            {isDisabledInvitesTask ? translate('no.friends.title') : title}
                        </div>
                        {getCounter()}
                        <div className="quest-card-reward">
                            <span className="quest-card-reward-label">{translate('quest.reward.label')}</span>
                            {getRewardBubble()}
                        </div>
                    </div>

                    {img ? <img className="quest-card-image" src={img}></img> : null}
                </div>

                {buttonText && !isDisabledInvitesTask ? (
                    <div className="quest-card-button-container">
                        <Button className="quest-card-button" onClick={onButtonClick}>
                            {buttonText}
                        </Button>
                    </div>
                ) : null}
            </div>

            <QuestInfoModal
                isOpened={openQuestInfoModal}
                mission={mission}
                onClose={() => setOpenQuestInfoModal(false)}
            />

            {loginsModalReward ? (
                <RewardDetailsModal
                    reward={loginsModalReward!}
                    isOpened={!!loginsModalReward}
                    onClose={() => setLoginsModalReward(undefined)}
                />
            ) : null}
        </>
    );
});
